import type { MutableRefObject, HTMLAttributes, ReactElement, ReactNode } from "react";

import type { ColDef, GridApi, MenuItemDef } from "ag-grid-community";
import { useGridMenuItem } from "ag-grid-react";

import {
  IconChatAttention,
  IconFile,
  IconInfo,
  IconLoader,
  IconRefresh,
  IconResizeColumn,
  IconSettings,
  IconSurfaceFloor,
  IconSurfaceLeft,
  IconSurfaceRight,
} from "@synthesis/shared-icons";
import { FileIconComponent, Tooltip, getTableColumnListFilter, type TableListColumnFilterItem } from "@synthesis/shared-ui";

import type { FileMetaDto } from "../../../api";
import { DocumentsTypes } from "../../../types";
import type { DrawingCollectionRow, DrawingPlane } from "../../drawings-collection/drawings-collection.lib";
import { isFileId, isUserId } from "../hooks";
import { ITEM_CLASS_NAME, type FileMetaWithExtras, type ModelStatusKind, type TableRow } from "./table-view.lib";

// ---------------------------------- TYPES ----------------------------------

export type DrawingsAsyncData = {
  drawings: DrawingCollectionRow[];
  userNamesById: Record<string, string | null>;
  ifcNamesById: Record<string, string | null>;
};

type BuildTableColumnsParams = {
  isDrawingsTab: boolean;
  isModelsPicker: boolean;
  isDocsTab: boolean;
  drawingsColumns: ColDef<TableRow>[];
  sourceFiles: FileMetaDto[];
  pdfPreviewsByFileId: Map<string, { pageCount: number; previewSrc: string | null }>;
  userNamesById: Record<string, string | null>;
  modelStatusesById: Record<string, ModelStatusKind>;
};

// ---------------------------------- CELLS ----------------------------------

type CustomMenuItemProps = {
  action?: () => void;
  icon?: ReactNode;
  name?: string;
};

const CustomSeparator = () => {
  return <div className="bg-grey-300 h-px" />;
};

const Item = ({ className: _className, ...props }: HTMLAttributes<HTMLDivElement>) => <div className={ITEM_CLASS_NAME} {...props} />;

const CustomMenuItem = (props: CustomMenuItemProps) => {
  useGridMenuItem({
    configureDefaults: () => true,
  });

  return (
    <Item onClick={props.action}>
      <span>{props.icon}</span>
      <span>{props.name}</span>
    </Item>
  );
};

const PlaneIcon = ({ plane }: { plane: DrawingPlane }) => {
  const className = "text-brand-40 size-6 shrink-0";
  if (plane === "Разрез YZ") return <IconSurfaceLeft className={className} />;
  if (plane === "План XY") return <IconSurfaceFloor className={className} />;
  return <IconSurfaceRight className={className} />;
};

const CellTooltip = ({ description, children }: { description?: string | null; children: ReactElement }) => {
  if (!description) {
    return children;
  }

  return (
    <Tooltip arrowSide="top" arrowAlign="center" description={description}>
      {children}
    </Tooltip>
  );
};

const TextCell = ({ value }: { value?: string }) => {
  if (!value) {
    return null;
  }

  return (
    <CellTooltip description={value}>
      <div className="flex h-full w-full min-w-0 items-center">
        <span className="block min-w-0 truncate">{value}</span>
      </div>
    </CellTooltip>
  );
};

const AuthorNameCell = ({ authorId, names }: { authorId?: string; names: Record<string, string | null> }) => {
  if (!authorId) return null;

  if (!isUserId(authorId)) {
    return <TextCell value={authorId} />;
  }

  const name = names[authorId];
  if (name === undefined) {
    return (
      <div className="flex h-full items-center">
        <div className="bg-grey-300 h-4 w-full animate-pulse rounded" />
      </div>
    );
  }

  return <TextCell value={name || authorId} />;
};

const IfcNameCell = ({ fileIds, names }: { fileIds?: string[]; names: Record<string, string | null> }) => {
  if (!fileIds?.length) return null;

  const labels = fileIds.map((fileId) => {
    if (!isFileId(fileId)) return fileId;
    return names[fileId];
  });

  if (labels.some((label) => label === undefined)) {
    return (
      <div className="flex h-full items-center">
        <div className="bg-grey-300 h-4 w-full animate-pulse rounded" />
      </div>
    );
  }

  const text = labels.filter(Boolean).join(", ");
  if (!text) return null;

  return <TextCell value={text} />;
};

// ---------------------------------- CONSTANTS ----------------------------------

export const MENU_ITEMS: MenuItemDef[] = [
  {
    name: "Автоматически задавать размер всем столбцам",
    action: (props: { api: GridApi }) => props.api.autoSizeAllColumns(),
    menuItem: CustomMenuItem,
    icon: (<IconResizeColumn className="text-neutrals-60 size-6" />) as unknown as Element,
  },
  {
    name: "Сепаратор",
    menuItem: CustomSeparator,
  },
  {
    name: "Выбрать столбцы",
    action: (props: { api: GridApi }) => props.api.showColumnChooser(),
    menuItem: CustomMenuItem,
    icon: (<IconSettings className="text-neutrals-60 size-6" />) as unknown as Element,
  },
  {
    name: "Сбросить столбцы",
    action: (props: { api: GridApi }) => props.api.resetColumnState(),
    menuItem: CustomMenuItem,
    icon: (<IconRefresh className="text-neutrals-60 size-6" />) as unknown as Element,
  },
];

// ---------------------------------- COLUMNS ----------------------------------

export const buildDrawingsColumns = (drawingsAsyncDataRef: MutableRefObject<DrawingsAsyncData>): ColDef<TableRow>[] => {
  return [
    {
      headerName: "Файл",
      field: "fileName",
      flex: 1,
      minWidth: 220,
      mainMenuItems: MENU_ITEMS,
      cellRenderer: ({ data }: { data?: TableRow }) => {
        if (!data) return null;
        const label = data.kind ? `${data.file.baseName}, ${data.kind}` : data.file.baseName;
        return (
          <CellTooltip description={label}>
            <div className="flex h-full w-full min-w-0 items-center gap-3">
              <FileIconComponent className="size-6 shrink-0" ext="pdf" />
              <span className="paragraph-base text-type-common min-w-0 truncate">{data.file.baseName}</span>
            </div>
          </CellTooltip>
        );
      },
    },
    {
      headerName: "Привязка к .IFC",
      field: "ifcBinding",
      width: 228,
      mainMenuItems: MENU_ITEMS,
      cellRenderer: ({ value }: { value?: string[] }) => <IfcNameCell fileIds={value} names={drawingsAsyncDataRef.current.ifcNamesById} />,
    },
    {
      headerName: "Плоскость",
      field: "plane",
      width: 143,
      mainMenuItems: MENU_ITEMS,
      cellRenderer: ({ data }: { data?: TableRow }) => {
        if (!data?.plane) return null;
        return (
          <CellTooltip description={data.plane}>
            <div className="flex h-full w-full min-w-0 items-center gap-3">
              <PlaneIcon plane={data.plane} />
              <span className="paragraph-base text-type-common min-w-0 truncate whitespace-nowrap">{data.plane}</span>
            </div>
          </CellTooltip>
        );
      },
    },
    {
      headerName: "Эксперт",
      field: "expert",
      width: 143,
      mainMenuItems: ["columnFilter", "separator", ...MENU_ITEMS] as ColDef<TableRow>["mainMenuItems"],
      cellRenderer: ({ data }: { data?: TableRow }) => <AuthorNameCell authorId={data?.expert} names={drawingsAsyncDataRef.current.userNamesById} />,
      ...getTableColumnListFilter<TableRow>({
        getItems: async () => {
          const { drawings: currentDrawings, userNamesById: currentUserNamesById } = drawingsAsyncDataRef.current;
          const byId = new Map<string, TableListColumnFilterItem>();

          for (const drawing of currentDrawings) {
            const id = drawing.expert;
            if (!id || byId.has(id)) continue;
            const name = currentUserNamesById[id];
            byId.set(id, { id, value: id, label: name || id });
          }

          return [...byId.values()].toSorted((a, b) => String(a.label).localeCompare(String(b.label), "ru"));
        },
        getItemId: (item) => String(item.id ?? item.value ?? ""),
        getItemLabel: (item) => {
          const id = String(item.id ?? item.value ?? "");
          if (!id) return "Не указан";
          const name = drawingsAsyncDataRef.current.userNamesById[id];
          return name || item.label || id;
        },
        getQueryKey: ({ search }) => {
          const names = drawingsAsyncDataRef.current.userNamesById;
          return [
            "drawings-expert-filter",
            search,
            Object.entries(names)
              .toSorted(([a], [b]) => a.localeCompare(b))
              .join("|"),
          ];
        },
        withAllSelectCheckbox: false,
        withSearch: true,
      }),
    },
    {
      headerName: "Дата",
      field: "date",
      width: 138,
      mainMenuItems: MENU_ITEMS,
    },
    {
      headerName: "Время",
      field: "time",
      width: 106,
      mainMenuItems: MENU_ITEMS,
    },
  ];
};

export const buildTableColumns = ({
  isDrawingsTab,
  isModelsPicker,
  isDocsTab,
  drawingsColumns,
  sourceFiles,
  pdfPreviewsByFileId,
  userNamesById,
  modelStatusesById,
}: BuildTableColumnsParams): ColDef<TableRow>[] => {
  if (isDrawingsTab) {
    return drawingsColumns;
  }

  const fileColumn: ColDef<TableRow> = {
    headerName: "Файл",
    field: "fileName",
    flex: isModelsPicker ? 1 : undefined,
    minWidth: isModelsPicker ? 280 : undefined,
    mainMenuItems: MENU_ITEMS,
    cellRenderer: ({ value }: { value?: FileMetaWithExtras }) => {
      if (!value) return null;
      const liveFile = sourceFiles.find((f) => f.id === value.id) || sourceFiles.find((f) => f.baseName === value.baseName) || null;
      const liveProgress = (liveFile as FileMetaWithExtras | null)?.progress ?? value.progress;
      const progress = typeof liveProgress === "number" && liveProgress > 0 ? `${liveProgress.toFixed(0)} %` : null;
      const isPdf = value.type === DocumentsTypes.pdf_document || value.ext?.toLowerCase() === "pdf";
      const pageCount = pdfPreviewsByFileId.get(value.id)?.pageCount ?? 0;
      const statusKind = modelStatusesById[value.id];

      return (
        <CellTooltip description={value.baseName}>
          <div className="flex h-full w-full min-w-0 items-center gap-2">
            <FileIconComponent className="size-6 shrink-0" ext={value.ext ?? undefined} />
            <span className="min-w-0 truncate">{value.baseName}</span>
            {statusKind ? (
              <span className="inline-flex size-5 shrink-0 items-center justify-center">
                {statusKind === "error" ? (
                  <IconInfo className="text-system-error-70 size-full" />
                ) : (
                  <IconLoader className="text-system-info-70 size-full animate-spin" aria-hidden />
                )}
              </span>
            ) : null}
            {isPdf && pageCount > 0 ? (
              <span className="text-brand-30 inline-flex shrink-0 items-center gap-1">
                <IconFile className="size-5" /> {pageCount}
              </span>
            ) : (
              (value.markupsCount ?? 0) > 0 && (
                <span className="text-system-error-50 inline-flex shrink-0 items-center gap-1">
                  <IconChatAttention className="size-5" /> {value.markupsCount}
                </span>
              )
            )}
            {progress && <span className="inline-flex shrink-0 items-center gap-1">{progress}</span>}
          </div>
        </CellTooltip>
      );
    },
  };

  if (isModelsPicker) {
    return [
      fileColumn,
      {
        headerName: "Версия",
        field: "version",
        width: 143,
        mainMenuItems: MENU_ITEMS,
      },
      {
        headerName: "Дата",
        field: "date",
        width: 138,
        mainMenuItems: MENU_ITEMS,
      },
      {
        headerName: "Время",
        field: "time",
        width: 106,
        mainMenuItems: MENU_ITEMS,
      },
    ];
  }

  if (isDocsTab) {
    return [
      fileColumn,
      {
        headerName: "Автор",
        field: "author",
        mainMenuItems: MENU_ITEMS,
        cellRenderer: ({ value }: { value?: string }) => <AuthorNameCell authorId={value} names={userNamesById} />,
      },
      {
        headerName: "Дата",
        field: "date",
        mainMenuItems: MENU_ITEMS,
      },
      {
        headerName: "Время",
        field: "time",
        mainMenuItems: MENU_ITEMS,
      },
      {
        headerName: "Размер",
        field: "size",
        mainMenuItems: MENU_ITEMS,
      },
    ];
  }

  return [
    fileColumn,
    {
      headerName: "Версия",
      field: "version",
      mainMenuItems: MENU_ITEMS,
    },
    {
      headerName: "Дата загрузки",
      field: "date",
      mainMenuItems: MENU_ITEMS,
    },
    {
      headerName: "Время загрузки",
      field: "time",
      mainMenuItems: MENU_ITEMS,
    },
    {
      headerName: "Размер",
      field: "size",
      mainMenuItems: MENU_ITEMS,
    },
  ];
};
